import React, { useEffect, useState, useRef } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import {
  setDashboardData,
  fetchAllPolicyTypeAction,
  fetchTaskTypeAction,
  fetchTaskDataAction,
  fetchPriorityTaskAction,
  userPrefrenceAction,
  getUserPrefrenceAction,
} from "../../../redux/actions/dashboardAction";

import {
  selectPolicyTypeCounts,
  selectPolicyTypeByPercentage,
  selectPolicyTypeByPremium,
  selectPolicyTypeTotalCount,
  selectQuoteStatusCount,
  selectQuoteStatusCountPercentage,
  selectQuoteTotalCount,
  selectTaskTotalCount,
  selectTaskCount,
  selectTaskCountByPercentage,
  selectPriorityTaskTotalCount,
  selectPriorityTaskCount,
  selectPriorityTaskPercentage,
  selectTaskTypeCount,
  selectOpenTaskCount,
  selectOpenTaskPercentage
} from '../../../redux/selectors/dashboardSelectors';

import { getAllUserPermissionAction } from "../../../redux/actions/locationActions";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  Select,
  FormControl,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";

import QuoteTableData from "./TableQuote";
import DashboardPolicyData from "./DashboardPolicyData";
import DashboardTaskList from "./DashboardTaskList";
import DashboardTaskPriority from "./DashboardTaskPriority";
import DashboardPolicyType from "./DashboardPolicyType";
import DashboardTypeOfTaskOpen from "./DashboardTypeOfTaskOpen";

const PieStatCard = ({
  subtitle = "Statistics",
  title = "",
  subtitleId = "statistics",
  titleId,
  totalLabel = "Total",
  totalId = "totalcount",
  totalValue = 0,

  selected = false,
  onClick,

  data = [],
  controls = null,

 
  containerSx = {},
  bodyHeightMd = "200px",
  pieWidthDesktop = 500,
  pieHeightDesktop = 450,
  pieWidthMobile = 250,
  pieHeightMobile = 250,
  legendSx = {},
  computePercent = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const safeData = Array.isArray(data) ? data : [];
  const totalForCompute = safeData.reduce(
    (sum, it) => sum + (Number(it?.value) || 0),
    0
  );

  const series = [
    {
      data: safeData.map((it, idx) => ({
        id: idx,
        value: Number(it?.value) || 0,
        color: it?.color || "#ccc",
      })),
    },
  ];

  return (
    <Box sx={{ width: "100%", padding: "10px", boxSizing: "border-box", ...containerSx }}>
      <Paper
        className={
          selected
            ? "dashboard-box-container-active dashboard-box-container"
            : "dashboard-box-container"
        }
        onClick={onClick}
        sx={{ cursor: onClick ? "pointer" : "default", borderColor: theme.palette.primary.main }}
      >
        {/* Header */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            fontFamily: '"Poppins", sans-serif',
            borderBottom: "#E5E5EF solid 1px",
            paddingBottom: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              flexFlow: "column",
              alignItems: " start",
            }}
          >
            <span id={subtitleId}>{subtitle}</span>
            <strong id={titleId}>{title}</strong>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              flexFlow: "column",
              alignItems: " end",
            }}
          >
            <span id={totalId}>{totalLabel}</span>
            <strong>{totalValue ?? 0}</strong>
          </Box>
        </Box>

        {controls && <Box sx={{ mt: 1 }}>{controls}</Box>}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: { xs: "column", md: "row" },
            height: { xs: "auto", md: bodyHeightMd },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "30%" },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <PieChart
              series={series}
              width={isMobile ? pieWidthMobile : pieWidthDesktop}
              height={isMobile ? pieHeightMobile : pieHeightDesktop}
              sx={{ transform: "scale(1.0)", transformOrigin: "center" }}
            />
          </Box>

          <Box
            sx={{
              width: { xs: "100%", md: "70%" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              ...legendSx,
            }}
          >
            {safeData.map((item, idx) => {
              const value = Number(item?.value) || 0;
              const pct =
                typeof item?.percentage === "number"
                  ? item.percentage
                  : computePercent && totalForCompute > 0
                  ? (value / totalForCompute) * 100
                  : undefined;

              return (
                <Typography
                  key={idx}
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "14px",
                  }}
                >
                  <span>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        backgroundColor: item?.color || "#ccc",
                        marginRight: 8,
                        borderRadius: "50%",
                      }}
                    ></span>
                    {item?.name}:
                  </span>
                  <span>
                    {value}
                    {typeof pct === "number" ? ` (${pct.toFixed(2)}%)` : ""}
                  </span>
                </Typography>
              );
            })}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [chartType, setChartType] = useState("count");
  const [policyChartType, setPolicyChartType] = useState("typecount");
  const show_data = useSelector((state) => state?.dashboardData?.show_data || null);
  const [showData, setShowData] = useState(false);

  useEffect(() => {
    dispatch(getUserPrefrenceAction('dashboard_graphs'))
  }, [dispatch])

  useEffect(() => {
    if (show_data?.key === "dashboard_graphs") {
      const newValue = show_data?.value === true || show_data?.value === "true";
      if (newValue !== showData) {
        setShowData(newValue);
      }
    }
  }, [show_data, dispatch]);


  const handleToggle = (event) => {
    setShowData(event.target.checked);
    dispatch(userPrefrenceAction({ key: "dashboard_graphs", value: event.target.checked}))
  };

  const policyDataRef = useRef(null);
  const policyTypeRef = useRef(null);
  const quoteDataRef = useRef(null);
  const taskDataRef = useRef(null);
  const priorityTaskRef = useRef(null);
  const openTaskRef = useRef(null);

  useEffect(() => {
    dispatch(setDashboardData());
    dispatch(fetchAllPolicyTypeAction());
    dispatch(fetchTaskTypeAction());
    dispatch(fetchPriorityTaskAction());
    dispatch(fetchTaskDataAction());
    dispatch(getAllUserPermissionAction());
  }, [dispatch]);

  const transformKeys = (obj) => {
    const newObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = key.replace(/\s+/g, "").toLowerCase();
        newObj[newKey] = obj[key];
      }
    }
    return newObj;
  };

  const policyTypeCounts = useSelector(selectPolicyTypeCounts);
  const policyTypeByPercentage = useSelector(selectPolicyTypeByPercentage);
  const policyTypeByPremium = useSelector(selectPolicyTypeByPremium);
  const policy_type_total_count = useSelector(selectPolicyTypeTotalCount);
  const quoteStatusCount = useSelector(selectQuoteStatusCount);
  const quoteStatusCountPercentage = useSelector(selectQuoteStatusCountPercentage);
  const quote_total_count = useSelector(selectQuoteTotalCount);
  const policyTotalCounts =
    useSelector(
      (state) => state.dashboardData.policies?.totalCount
    ) || [];
  const policystatusCounts =
    useSelector(
      (state) => state.dashboardData.policies?.lineOfBusinessCounts
    ) || [];
  const policyDataByPremium =
    useSelector(
      (state) => state.dashboardData.policies?.lineOfBusinessPremium
    ) || [];

  const taskTotalCount = useSelector(selectTaskTotalCount);
  const taskCount = useSelector(selectTaskCount);
  const taskCountByPercentage = useSelector(selectTaskCountByPercentage);

  const priority_task_total_count = useSelector(selectPriorityTaskTotalCount);
  const priority_task_count = useSelector(selectPriorityTaskCount);
  const priority_task_percentage = useSelector(selectPriorityTaskPercentage);

  const task_type_count = useSelector(selectTaskTypeCount);
  const open_task_count = useSelector(selectOpenTaskCount);
  const open_task_percentage = useSelector(selectOpenTaskPercentage);

  const policydata = transformKeys(policystatusCounts);
  const policy_by_premium = transformKeys(policyDataByPremium);

  const policyTypeData = transformKeys(policyTypeCounts);
  const policyTypeByPercentageCount = transformKeys(policyTypeByPercentage);
  const policyTypePremium = transformKeys(policyTypeByPremium);

  const opentaskcount = transformKeys(open_task_count);
  const opentaskpercentage = transformKeys(open_task_percentage);

  const task_count = transformKeys(taskCount);
  const task_count_percentage = transformKeys(taskCountByPercentage);

  const priortyTaskCount = transformKeys(priority_task_count);
  const priortyTaskCountPercentage = transformKeys(priority_task_percentage);

  const quoteCount = transformKeys(quoteStatusCount);
  const quotePercentageCount = transformKeys(quoteStatusCountPercentage);

  const handleChartClick = (index) => {
    setSelectedTab(index);
    switch (index) {
      case 1:
        policyDataRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      case 2:
        policyTypeRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      case 3:
        quoteDataRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      case 4:
        taskDataRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      case 5:
        priorityTaskRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      case 6:
        openTaskRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      default:
        break;
    }
  };

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
  };

  const pieChartsData = {
    policiesData:
      chartType === "count"
        ? [
            {
              id: "personalauto",
              name: "Personal Auto",
              value: policydata.personalauto,
              color: "#344BFD",
            },
            {
              id: "commercialauto",
              name: "Commercial Auto",
              value: policydata.commercialauto,
              color: "#FFBB28",
            },
            {
              id: "homeowners",
              name: "Homeowners",
              value: policydata.homeowners,
              color: "#FF8042",
            },
            {
              id: "health",
              name: "Health",
              value: policydata.health,
              color: "#92CA67",
            },
            {
              id: "generalliability",
              name: "General Liability",
              value: policydata.generalliability,
              color: "#72413A",
            },
          ]
        : [
            {
              id: "personalauto",
              name: "Personal Auto",
              value: policy_by_premium.personalauto,
              color: "#344BFD",
            },
            {
              id: "Commercialauto",
              name: "Commercial Auto",
              value: policy_by_premium.commercialauto,
              color: "#FFBB28",
            },
            {
              id: "homeowners",
              name: "Homeowners",
              value: policy_by_premium.homeowners,
              color: "#FF8042",
            },
            {
              id: "health",
              name: "Health",
              value: policy_by_premium.health,
              color: "#92CA67",
            },
            {
              id: "generalliability",
              name: "General Liability",
              value: policy_by_premium.generalliability,
              color: "#72413A",
            },
          ],
    policyTypeData:
      policyChartType === "typecount"
        ? [
            {
              id: "newbusinesstype",
              name: "New Business",
              value: policyTypeData.newbusiness,
              percentage: policyTypeByPercentageCount.newbusiness,
              color: "#F68D2B",
            },
            {
              id: "renewaltype",
              name: "Renewal",
              value: policyTypeData.renewal,
              percentage: policyTypeByPercentageCount.renewal,
              color: "#F4A79D",
            },
            {
              id: "rewritetype",
              name: "Rewrite",
              value: policyTypeData.rewrite,
              percentage: policyTypeByPercentageCount.rewrite,
              color: "#344BFD",
            },
          ]
        : [
            {
              id: "newbusiness",
              name: "New Business",
              value: policyTypePremium.newbusiness,
              color: "#F68D2B",
            },
            {
              id: "renewal",
              name: "Renewal",
              value: policyTypePremium.renewal,
              color: "#F4A79D",
            },
            {
              id: "rewrite",
              name: "Rewrite",
              value: policyTypePremium.rewrite,
              color: "#344BFD",
            },
          ],
    quoteData: [
      {
        id: "sold",
        name: "Sold",
        value: quoteCount?.sold,
        percentage: quotePercentageCount?.sold,
        color: "#344BFD",
      },
      {
        id: "notsold",
        name: "Not Sold",
        value: quoteCount?.notsold,
        percentage: quotePercentageCount.notsold,
        color: "#FF8042",
      },
    ],
    taskData: [
      {
        id: "close",
        name: "Close",
        value: task_count.closed,
        percentage: task_count_percentage.closed,
        color: "#344BFD",
      },
      {
        id: "open",
        name: "Open",
        value: task_count.open,
        percentage: task_count_percentage.open,
        color: "#FFBB28",
      },
      {
        id: "duetaskdate",
        name: "Due Task Date",
        value: task_count.duetaskdate,
        percentage: task_count_percentage.duetaskdate,
        color: "#FF8042",
      },
    ],
    priorityTaskData: [
      {
        id: "urgentdata",
        name: "Urgent",
        value: priortyTaskCount.urgent,
        percentage: priortyTaskCountPercentage.urgent,
        color: "#344BFD",
      },
      {
        id: "highPrioritydata",
        name: "High Priority",
        value: priortyTaskCount.high,
        percentage: priortyTaskCountPercentage.high,
        color: "#FFBB28",
      },
      {
        id: "mediumprioritydata",
        name: "Medium Priority",
        value: priortyTaskCount.medium,
        percentage: priortyTaskCountPercentage.medium,
        color: "#FF8042",
      },
    ],
  };

  const taskLabelColorMap = {
    pendingcancellationpayment: {
      name: "Pending Cancellation Payment",
      color: "#FFB84C",
    },
    crosssellingcommercial: {
      name: "Cross Selling Commercial",
      color: "#1B9CFC",
    },
    cancelledpolicy: {
      name: "Cancelled Policy",
      color: "#F76E6E",
    },
    applicationpendingsignature: {
      name: "Pending Signature",
      color: "#F8EFBA",
    },
    winback: {
      name: "Win Back",
      color: "#58B19F",
    },
    billing: {
      name: "Billing",
      color: "#574B90",
    },
    pendingcancellationforuw: {
      name: "Pending Cancellation UW",
      color: "#E58E26",
    },
    crosssellinghealth: {
      name: "Cross Selling Health",
      color: "#2C3A47",
    },
    crosssellinghomeowners: {
      name: "Cross Selling Homeowners",
      color: "#BDC581",
    },
    followupquote: {
      name: "Follow Up Quote",
      color: "#3B3B98",
    },
    crosssellingauto: {
      name: "Cross Selling Auto",
      color: "#C44569",
    },
    renewalofferreview: {
      name: "Renewal Offer Review",
      color: "#00B894",
    },
  };

  const typeofTaskOpenData = Object.keys(opentaskcount).map((key) => ({
    id: key,
    name: taskLabelColorMap[key]?.name || key,
    value: opentaskcount[key],
    percentage: opentaskpercentage[key] || 0,
    color: taskLabelColorMap[key]?.color || "#ccc",
  }));


  const renderPieChartPolicy = (label, chartData, index) => {
    const policiesControls = (
      <RadioGroup
        row
        aria-label="chart-type"
        name="chart-type-policies"
        value={chartType}
        onChange={(e) => setChartType(e.target.value)}
        id="charttyperadiogroup-policies"
      >
        <FormControlLabel
          value="count"
          control={<Radio id="charttypecount-policies" />}
          label="By Count"
        />
        <FormControlLabel
          value="premium"
          control={<Radio id="charttypepremium-policies" />}
          label="By Premium"
        />
      </RadioGroup>
    );

    return (
      <PieStatCard
        subtitle="Statistics"
        title="Policies"
        subtitleId="statistics-policies"
        titleId="policies-title"
        totalLabel={`Total ${chartType === "count" ? "Count" : "Premium"}`}
        totalId="total-policies"
        totalValue={policyTotalCounts}
        data={chartData}
        selected={selectedTab === index}
        onClick={() => handleChartClick(index)}
        controls={policiesControls}
        computePercent={chartType === "count"}
        containerSx={{
          padding: {
            ms: "0 50px",
            xs: "10px",
          },
          boxSizing: "border-box",
        }}
        bodyHeightMd="200px"
      />
    );
  };

  const renderPieChartPolicyType = (label, chartData, index) => {
    const policyTypeControls = (
      <RadioGroup
        row
        aria-label="chart-type-policy-type"
        name="chart-type-policy-type"
        value={policyChartType}
        onChange={(e) => setPolicyChartType(e.target.value)}
        id="charttyperadiogroup-policy-type"
      >
        <FormControlLabel
          value="typecount"
          control={<Radio id="charttypecount-policy-type" />}
          label="By Count"
        />
        <FormControlLabel
          value="premium"
          control={<Radio id="charttypepremium-policy-type" />}
          label="By Premium"
        />
      </RadioGroup>
    );

    return (
      <PieStatCard
        subtitle="Statistics"
        title="Policy Type"
        subtitleId="statistics-policy-type"
        titleId="policytype-title"
        totalLabel={`Total ${policyChartType === "typecount" ? "Count" : "Premium"}`}
        totalId="totalcount-policy-type"
        totalValue={policy_type_total_count}
        data={chartData}
        selected={selectedTab === index}
        onClick={() => handleChartClick(index)}
        controls={policyTypeControls}
        computePercent={policyChartType === "typecount"}
        bodyHeightMd="200px"
      />
    );
  };

  const renderPieChartQuote = (label, chartData, index) => {
    return (
      <PieStatCard
        subtitle="Statistics"
        title="Quote"
        subtitleId="statistics-quote"
        titleId="quote-title"
        totalLabel="Total Count"
        totalId="totalcount-quote"
        totalValue={quote_total_count}
        data={chartData}
        selected={selectedTab === index}
        onClick={() => handleChartClick(index)}
        bodyHeightMd="250px"
        computePercent={false} 
      />
    );
  };

  const renderPieChartTask = (label, chartData, index) => {
    return (
      <PieStatCard
        subtitle="Statistics"
        title="Task"
        subtitleId="statistics-task"
        titleId="task-title"
        totalLabel="Total Count"
        totalId="totalcount-task"
        totalValue={taskTotalCount}
        data={chartData}
        selected={selectedTab === index}
        onClick={() => handleChartClick(index)}
        bodyHeightMd="200px"
        computePercent={false} 
      />
    );
  };

  const renderPieChartPriorityTask = (label, chartData, index) => {
    return (
      <PieStatCard
        subtitle="Statistics"
        title="Priority Task"
        subtitleId="statistics-priority-task"
        titleId="prioritytask-title"
        totalLabel="Total Count"
        totalId="totalcount-priority-task"
        totalValue={priority_task_total_count}
        data={chartData}
        selected={selectedTab === index}
        onClick={() => handleChartClick(index)}
        bodyHeightMd="200px"
        computePercent={false} 
      />
    );
  };

  const renderPieChartOpenTask = (label, chartData, index) => {
    return (
      <PieStatCard
        subtitle="Statistics"
        title="Type of Task Open"
        subtitleId="statistics-open-task"
        titleId="typeoftaskopens-title"
        totalLabel="Total Count"
        totalId="totalcount-open-task"
        totalValue={task_type_count}
        data={chartData}
        selected={selectedTab === index}
        onClick={() => handleChartClick(index)}
        bodyHeightMd="200px"
        legendSx={{
          height: { xs: "auto", md: "150px" },
          overflow: "auto",
        }}
        computePercent={false} 
      />
    );
  };

  return (
    <Box sx={{ padding: {
      md:"0 50px",
      sm:"0 10px"
    }, margin: "2rem auto 0 auto" }}>
      <Grid>
        <Typography
          id="dashboard-title"          
          variant="h2" gutterBottom
        >
          Dashboard
        </Typography>

        {/* Filter List and Show Data Toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            padding: "10px 20px",
            backgroundColor: "#f4f4f4",
            justifyContent: "flex-end",
            borderRadius: "8px",
            marginBottom: "20px",
            flexFlow:{
              sm: "column",
              md: "row"
            }
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
          <FormControl sx={{ minWidth: 200, margin: "0 15px" }}>
          <Select
            native
            name="dashboard-filter"
            value={selectedTab}
            onChange={(event) => handleChartClick(Number(event.target.value))}
            displayEmpty
            sx={{
              backgroundColor: "#fff",
              borderRadius: "5px",
            }}
          >
            <option value="" disabled>
              Select a Filter
            </option>
            <option value={1}>Policies</option>
            <option value={2}>Policy Type</option>
            <option value={3}>Quotes</option>
            <option value={4}>Tasks</option>
            <option value={5}>Priority Task</option>
            <option value={6}>Type of Task Open</option>
          </Select>
        </FormControl>

            {/* Show Data Toggle */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Typography variant="body1" sx={{ marginRight: "8px" }}>
                Show Data
              </Typography>
              <Switch 
                checked={showData} 
                onChange={handleToggle}
                id="show-data-switch"
                name="show-data-switch" 
              />
            </Box>
          </Box>
        </Box>

        {showData && (
          <Grid container spacing={1}>
            <Grid item md={4} lg={4}>
              {renderPieChartPolicy("Policies", pieChartsData.policiesData, 1)}
            </Grid>
            <Grid item md={4} lg={4}>
              {renderPieChartPolicyType(
                "Policy Type",
                pieChartsData.policyTypeData,
                2
              )}
            </Grid>
            <Grid item md={4} lg={4}>
              {renderPieChartQuote("Quotes", pieChartsData.quoteData, 3)}
            </Grid>
            <Grid item md={4} lg={4}>
              {renderPieChartTask("Tasks", pieChartsData.taskData, 4)}
            </Grid>
            <Grid item md={4} lg={4}>
              {renderPieChartPriorityTask(
                "Priority Task",
                pieChartsData.priorityTaskData,
                5
              )}
            </Grid>
            <Grid item md={4} lg={4}>
              {renderPieChartOpenTask("Type of Task Open", typeofTaskOpenData, 6)}
            </Grid>
          </Grid>
        )}

        <Box>
          <Grid container marginTop={3}>
            <Grid item xs={12}>
              <div ref={policyDataRef}>
                {selectedTab === 1 && (
                  <DashboardPolicyData handleRowClick={handleRowClick} />
                )}
              </div>
              <div ref={policyTypeRef}>
                {selectedTab === 2 && <DashboardPolicyType />}
              </div>
              <div ref={quoteDataRef}>
                {selectedTab === 3 && <QuoteTableData />}
              </div>
              <div ref={taskDataRef}>
                {selectedTab === 4 && <DashboardTaskList />}
              </div>
              <div ref={priorityTaskRef}>
                {selectedTab === 5 && <DashboardTaskPriority />}
              </div>
              <div ref={openTaskRef}>
                {selectedTab === 6 && <DashboardTypeOfTaskOpen />}
              </div>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Box>
  );
};

export default Dashboard;